import { TableRowSideSheetForm } from "../../../common/tables/view/TableRowSideSheetForm";
import React, {
    ChangeEvent,
    FC,
    PropsWithChildren,
    useCallback,
} from "react";
import { CostPayoutModifierCreateInput } from "../../../../../common/inputs/CostPayoutModifierInput";
import {
    SelectField,
    TextInputField,
} from "evergreen-ui";

interface Props {
    isShown: boolean;
    costPayoutModifier: Partial<CostPayoutModifierCreateInput>;
    mutationLoading: boolean;
    mutationCalled: boolean;
    mutationError: boolean;
    onInputChanged: (costPayoutModifier: Partial<CostPayoutModifierCreateInput>) => void;
    onDismiss: () => void;
    onSubmit: () => void;
}

export const CPMTableRowSideSheetForm: FC<Props> = ({
    isShown,
    costPayoutModifier,
    mutationLoading,
    mutationCalled,
    mutationError,
    onInputChanged,
    onDismiss,
    onSubmit,
}: PropsWithChildren<Props>) => {
    const onChange = ({ target: { value, name, type }}: ChangeEvent<HTMLInputElement>) => {
        onInputChanged({
            ...costPayoutModifier,
            [name]: (type === "number") ? parseInt(value, 10) : value,
        });
    };

    const onSelectChange = ({ target: { value, name }}: ChangeEvent<HTMLSelectElement>) => {
        let parsedValue: boolean | undefined;
        if (value === "true") {
            parsedValue = true;
        } else if (value === "false") {
            parsedValue = false;
        } else {
            parsedValue = undefined;
        }
        onInputChanged({
            ...costPayoutModifier,
            [name]: parsedValue,
        });
    };

    const isReadyToSubmit = useCallback((): boolean => {
        return costPayoutModifier.name !== undefined && costPayoutModifier.name.trim() !== "";
    }, [costPayoutModifier]);

    return (
        <TableRowSideSheetForm
            isShown={isShown}
            headerTitle={"Add Modifier"}
            mutationLoading={mutationLoading}
            mutationCalled={mutationCalled}
            mutationError={mutationError}
            canSubmit={isReadyToSubmit()}
            onDismiss={onDismiss}
            onSubmit={onSubmit}
        >
            <TextInputField
                name={"name"}
                label={"Name"}
                value={costPayoutModifier.name ?? ""}
                required={true}
                maxWidth={"300px"}
                isInvalid={costPayoutModifier.name !== undefined && costPayoutModifier.name.trim() === ""}
                onChange={onChange}
            />
            <TextInputField
                name={"conditionCountryCode"}
                label={"Country Code"}
                description={"2-character country code (e.g. US, CA)"}
                value={costPayoutModifier.conditionCountryCode ?? ""}
                maxWidth={"300px"}
                maxLength={2}
                onChange={onChange}
            />
            <TextInputField
                name={"conditionEmailDomains"}
                label={"Email Domains"}
                description={"Comma-separated list of email domains"}
                value={
                    Array.isArray(costPayoutModifier.conditionEmailDomains)
                        ? costPayoutModifier.conditionEmailDomains.join(", ")
                        : (costPayoutModifier.conditionEmailDomains ?? "")
                }
                maxWidth={"300px"}
                onChange={onChange}
            />
            <TextInputField
                name={"conditionSourceName"}
                label={"Source Name"}
                description={"Order source name filter (e.g. web, pos)"}
                value={costPayoutModifier.conditionSourceName ?? ""}
                maxWidth={"300px"}
                onChange={onChange}
            />
            <TextInputField
                name={"conditionFulfillmentService"}
                label={"Fulfillment Service"}
                description={"Fulfillment service filter"}
                value={costPayoutModifier.conditionFulfillmentService ?? ""}
                maxWidth={"300px"}
                onChange={onChange}
            />
            <SelectField
                name={"conditionIsBundle"}
                label={"Is Bundle"}
                width={"100%"}
                value={
                    costPayoutModifier.conditionIsBundle === true
                        ? "true"
                        : costPayoutModifier.conditionIsBundle === false
                            ? "false"
                            : ""
                }
                onChange={onSelectChange}
            >
                <option value={""}>Any</option>
                <option value={"true"}>Yes</option>
                <option value={"false"}>No</option>
            </SelectField>
            <TextInputField
                name={"priority"}
                label={"Priority"}
                type={"number"}
                step={1}
                value={costPayoutModifier.priority ?? 0}
                maxWidth={"300px"}
                onChange={onChange}
            />
        </TableRowSideSheetForm>
    );
};
